import axios from "axios";
import { supabase } from "../supabaseClient";

async function getAllTransactions(from, to, searchValue, sortBy, selectedDate) {
  // const limit = 10;
  // const fromCount = nextRange?.from;
  // const toCount = nextRange?.to;
  let query = supabase
    .rpc(
      "get_untaggedtransactions",
      { search_text: searchValue.replace(/,/g, "") },
      { count: "exact" }
    )
    .order("last_updated_at", {
      ascending: sortBy.includes("Oldest") ? true : false,
    })
    .range(from, to);

  if (
    selectedDate?.[0]?.value !== "" &&
    selectedDate?.[1]?.value !== "" &&
    selectedDate !== undefined
  ) {
    query = query
      .lte("last_updated_at", Date.parse(new Date(selectedDate?.[1]?.value)))
      .gte("last_updated_at", Date.parse(new Date(selectedDate?.[0]?.value)));
  }
  const { data, count, error } = await query;
  if (data) return { data, count };
  else throw error;
}

async function getAllTransactionsSummary(searchValue, selectedDate) {
  // get_untaggedtransactions times out when asked for the full set (or a large
  // range) in one call, so this pulls it in small pages instead.
  const CHUNK_SIZE = 50;
  let from = 0;
  let all = [];

  while (true) {
    let query = supabase
      .rpc("get_untaggedtransactions", {
        search_text: (searchValue || "").replace(/,/g, ""),
      })
      .order("last_updated_at", { ascending: false })
      .range(from, from + CHUNK_SIZE - 1);

    if (
      selectedDate?.[0]?.value !== "" &&
      selectedDate?.[1]?.value !== "" &&
      selectedDate !== undefined
    ) {
      query = query
        .lte("last_updated_at", Date.parse(new Date(selectedDate?.[1]?.value)))
        .gte("last_updated_at", Date.parse(new Date(selectedDate?.[0]?.value)));
    }

    const { data, error } = await query;
    if (error) throw error;

    all = all.concat(data || []);
    if (!data || data.length < CHUNK_SIZE) break;
    from += CHUNK_SIZE;
  }

  return all;
}

// Returns EVERY token/allotment row this payId has ever been placed against — not just the
// first match. A transaction can be tagged more than once across separate Manual Tag calls
// (partial-then-partial), so a single {type, data} result would hide all but one placement.
async function getTaggedTransactionTarget(payId) {
  if (!payId) return { tokens: [], allotments: [] };

  const [tokenRes, allotmentRes] = await Promise.all([
    supabase.from("TokenTransactions").select("*").eq("payId", payId).order("created_at"),
    supabase
      .from("AllotmentTransactions")
      .select("*, Inventory(unitNo, floorNo, Wings(wingName), Projects(projectName))")
      .eq("payId", payId)
      .order("created_at"),
  ]);

  return {
    tokens: tokenRes.data || [],
    allotments: allotmentRes.data || [],
  };
}

// Mirrors the exact eligibility rules the auto-tag waterfall uses
// (PAYMENTS-WEBHOOK-SNAPSHOT-NODE-API: TokenPayments.js / AllotmentPayments.js /
// SdrPayments.js / OcrGstPayments.js), so this list shows only dues that
// auto-tag would actually place money against — not a superset guess.
const DAY_MS = 86400000;

function computeAllotmentDue(a) {
  const isPreselectUnconfirmed =
    a.pricingType === "preselect" && a.preselectConfirmation !== "confirmed";

  if (a.status !== "Payment Complete") {
    // Bucket 2 (booking amount): eligible statuses only, and only while
    // something is actually still owed on the booking amount.
    const bookingDue = (a.bookingAmt?.total || 0) - (a.paidAmount || 0);
    if (bookingDue > 0) {
      return { ...a, dueFor: "Booking Amount", dueAmount: bookingDue };
    }
    return null;
  }

  // Buckets 3 & 4 (SDR, OCR/GST) only ever run against allotments already at
  // Payment Complete, and skip an unconfirmed preselect exactly like autotag does.
  if (isPreselectUnconfirmed) return null;

  const paidAmt = a.paidAmt || { ocr: 0, gst: 0, sdr: 0 };
  const sdrDue = (a.unitCost?.sdr || 0) - (paidAmt.sdr || 0);

  let ocrPercentage = 0.05;
  if (a.unitCost?.bsp > 0) {
    ocrPercentage = Number(a.unitCost.gst) / Number(a.unitCost.bsp);
  }
  // Note: skips the special 0.25 ocrRatio override OCRGSTPaymentByVirtualAcc
  // applies for CRM payment plan ids 163/165 — a rare case not replicated here.
  const totalOcr = Math.round((a.unitCost?.bsp || 0) * 0.1);
  const totalGst = Math.round((a.unitCost?.bsp || 0) * 0.1 * Number(ocrPercentage.toFixed(2)));
  const ocrGstDue = totalOcr + totalGst - ((paidAmt.ocr || 0) + (paidAmt.gst || 0));

  const dueFor = [];
  if (sdrDue > 0) dueFor.push("SDR");
  if (ocrGstDue > 0) dueFor.push("OCR/GST");
  if (dueFor.length === 0) return null;

  return {
    ...a,
    dueFor: dueFor.join(" + "),
    dueAmount: Math.max(sdrDue, 0) + Math.max(ocrGstDue, 0),
  };
}

async function getPendingDuesForLead(leadDisplayId) {
  if (!leadDisplayId) return { tokens: [], allotments: [] };

  const { data: leads, error: leadError } = await supabase
    .from("Leads")
    .select("leadId")
    .eq("leadDisplayId", leadDisplayId);
  if (leadError) throw leadError;

  const leadId = leads?.[0]?.leadId;
  if (!leadId) return { tokens: [], allotments: [] };

  const [tokenRes, allotmentRes] = await Promise.all([
    // Bucket 1 (token): only pending/partial — autotag never looks at any
    // other status (active, alloted, expired, refund*, adjusted, ...).
    supabase
      .from("EventTokenLeadRelations")
      .select("*")
      .eq("leadId", leadId)
      .in("status", ["pending", "partial"])
      .order("created_at"),
    supabase
      .from("AllotmentPayment")
      .select("*")
      .eq("leadId", leadId)
      .in("status", [
        "Payment Pending",
        "Partial Payment Done",
        "Preselected",
        "Cancellation Rejected",
        "Payment Complete",
      ])
      .order("created_at"),
  ]);

  // Same 24h expiry grace rule as TokenPaymentByVirtualAcc: a token with
  // nothing paid yet and older than a day never gets auto-tagged into.
  const tokens = (tokenRes.data || []).filter(
    (t) => t.paidAmount > 0 || Date.now() < t.created_at + DAY_MS
  );

  const allotments = (allotmentRes.data || [])
    .map(computeAllotmentDue)
    .filter(Boolean);

  return { tokens, allotments };
}

async function refundFromTransactionPayment(body) {
  return new Promise((resolve, reject) => {
    // process.env.REACT_APP_PAYMENT_LINK_URL
    // 'http://192.168.2.113:8000/'
    axios
      .post(
        process.env.REACT_APP_PAYMENT_LINK_URL +
          "payments/refundUntaggedTransaction",
        body
      )
      .then((resp) => {
        resolve(resp);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

async function autotagFromTransactionPayment(body) {
  return new Promise((resolve, reject) => {
    axios
      // CRM 2.0 M6-F3: through the backend (it adds the payments-service token); same body and answer
      .post(
        process.env.REACT_APP_PAYMENT_LINK_URL + "payments/autoTagUntagged",
        body,
        { headers: { Authorization: `Bearer ${supabase.auth.session()?.access_token}` } }
      )
      .then((resp) => {
        resolve(resp);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

async function manualTagFromTransactionPayment(body) {
  return new Promise((resolve, reject) => {
    axios
      .post(process.env.REACT_APP_PAYMENT_LINK_URL + "manualTag/tag", body)
      .then((resp) => {
        resolve(resp);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export {
  getAllTransactions,
  getAllTransactionsSummary,
  getTaggedTransactionTarget,
  getPendingDuesForLead,
  refundFromTransactionPayment,
  autotagFromTransactionPayment,
  manualTagFromTransactionPayment,
};
